import { Button, Input, Textarea } from '@tark/shared/components';
import { cn } from '@tark/shared/lib';
import type { TenantStudioCategory, TenantStudioLocation, TenantStudioSeason } from '@tark/shared/types';
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, DollarSign, MapPin, Plus, Settings, Trash2, Users } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import type {
  ProgramCatalogCategoryDraft,
  ProgramCatalogCoachOption,
  ProgramCatalogFormProps,
  ProgramCatalogSessionDraft,
} from './programCatalogTypes';
import {
  ageBandOptions,
  createCategoryDraft,
  createSessionDraft,
  programTypeOptions,
  sportOptions,
} from './programCatalogUtils';

type ProgramCatalogStep = 'basics' | 'sessions' | 'classes' | 'publish';

const formSteps: Array<{ key: ProgramCatalogStep; label: string }> = [
  { key: 'basics', label: 'Basics' },
  { key: 'sessions', label: 'Season blocks' },
  { key: 'classes', label: 'Classes' },
  { key: 'publish', label: 'Publish' },
];

export default function ProgramCatalogForm({
  formState,
  editingProgram,
  studioSettings,
  coachOptions,
  isSaving,
  error,
  onChange,
  onSubmit,
  onCancel,
}: ProgramCatalogFormProps) {
  const [activeStep, setActiveStep] = useState<ProgramCatalogStep>('basics');
  const [seasonYear, setSeasonYear] = useState(() => inferSeasonYear(formState.startsOn));
  const activeStepIndex = formSteps.findIndex((step) => step.key === activeStep);
  const activeLocations = useMemo(() => (studioSettings?.locations ?? []).filter((item) => item.active), [studioSettings?.locations]);
  const activeSeasons = useMemo(() => (studioSettings?.seasons ?? []).filter((item) => item.active), [studioSettings?.seasons]);
  const activeCategories = useMemo(() => (studioSettings?.categories ?? []).filter((item) => item.active), [studioSettings?.categories]);

  const setField = <K extends keyof typeof formState>(key: K, value: (typeof formState)[K]) => {
    onChange((current) => ({ ...current, [key]: value }));
  };

  const applySeasonPreset = (seasonId: string) => {
    const season = activeSeasons.find((item) => String(item.seasonId) === seasonId);
    if (!season) {
      return;
    }
    onChange((current) => ({
      ...current,
      seasonLabel: `${season.displayName} ${seasonYear}`.trim(),
      startsOn: dateFromSeasonPoint(seasonYear, season.startsMonth, season.startsDay) ?? current.startsOn,
      endsOn: dateFromSeasonPoint(resolveEndYear(seasonYear, season), season.endsMonth, season.endsDay) ?? current.endsOn,
    }));
  };

  const applyLocationPreset = (value: string) => {
    setField('locationLabel', value);
  };

  const applyCategoryPreset = (categoryId: string) => {
    const category = activeCategories.find((item) => String(item.categoryId) === categoryId);
    if (!category) {
      return;
    }

    onChange((current) => {
      const nextCategory = createCategoryDraftFromPreset(category, current.categories.length);
      const emptyIndex = current.categories.findIndex(isBlankCategoryDraft);
      const categories =
        emptyIndex >= 0
          ? current.categories.map((item, index) => (index === emptyIndex ? nextCategory : item))
          : [...current.categories, nextCategory];

      return {
        ...current,
        categories,
        programType: category.programType || current.programType,
        sportCode: category.sportCode || current.sportCode,
        defaultCapacity: current.defaultCapacity || (category.capacity != null ? String(category.capacity) : ''),
        priceLabel: current.priceLabel || category.priceLabel || (category.priceAmount != null ? `$${category.priceAmount}` : ''),
      };
    });
  };

  const updateSession = (id: string, patch: Partial<ProgramCatalogSessionDraft>) => {
    onChange((current) => ({
      ...current,
      sessions: current.sessions.map((session) => (session.id === id ? { ...session, ...patch } : session)),
    }));
  };

  const updateCategory = (id: string, patch: Partial<ProgramCatalogCategoryDraft>) => {
    onChange((current) => ({
      ...current,
      categories: current.categories.map((category) => (category.id === id ? { ...category, ...patch } : category)),
    }));
  };

  const readiness = useMemo(
    () => [
      { label: 'Name', done: Boolean(formState.name.trim()) },
      { label: 'Dates', done: Boolean(formState.startsOn && formState.endsOn) },
      { label: 'Fee', done: Boolean(formState.priceLabel || formState.categories.some((item) => item.priceAmount || item.priceLabel)) },
      { label: 'Season blocks', done: formState.sessions.some((item) => item.label.trim().length > 0) },
      { label: 'Classes', done: formState.categories.some((item) => item.label.trim().length > 0) },
      { label: 'Slots', done: Boolean(formState.defaultCapacity) },
    ],
    [formState.categories, formState.defaultCapacity, formState.endsOn, formState.name, formState.priceLabel, formState.sessions, formState.startsOn]
  );
  const readyCount = readiness.filter((item) => item.done).length;

  const goNext = () => {
    const nextStep = formSteps[Math.min(activeStepIndex + 1, formSteps.length - 1)];
    setActiveStep(nextStep.key);
  };

  const goBack = () => {
    const previousStep = formSteps[Math.max(activeStepIndex - 1, 0)];
    setActiveStep(previousStep.key);
  };

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (activeStep === 'publish') {
          onSubmit('publish');
          return;
        }
        goNext();
      }}
    >
      <div className="grid gap-5 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="space-y-4">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-1">
            {formSteps.map((step, index) => (
              <StepButton
                key={step.key}
                active={activeStep === step.key}
                complete={index < activeStepIndex}
                label={step.label}
                onClick={() => setActiveStep(step.key)}
              />
            ))}
          </div>

          <div className="rounded-lg border border-border bg-surface px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Ready</p>
            <p className="mt-1 text-2xl font-semibold text-text">
              {readyCount}/{readiness.length}
            </p>
            <div className="mt-3 space-y-2">
              {readiness.map((item) => (
                <CheckRow key={item.label} label={item.label} done={item.done} compact />
              ))}
            </div>
          </div>
        </aside>

        <div className="min-w-0">
          {activeStep === 'basics' ? (
            <FormSection title="Basics" subtitle="Set the operational shape first. Public copy comes at publish time.">
              <FormGroup title="Program identity">
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Class/program name"
                    hint="Internal and coach-facing name."
                    value={formState.name}
                    onChange={(event) => setField('name', event.target.value)}
                  />
                  {activeSeasons.length > 0 ? (
                    <SelectField
                      label="Season preset"
                      value=""
                      options={[
                        { value: '', label: 'Choose season' },
                        ...activeSeasons.map((season) => ({ value: String(season.seasonId), label: formatSeasonPresetLabel(season) })),
                      ]}
                      onChange={applySeasonPreset}
                    />
                  ) : (
                    <Input
                      label="Season"
                      hint="Example: Summer 2026."
                      value={formState.seasonLabel}
                      onChange={(event) => setField('seasonLabel', event.target.value)}
                    />
                  )}
                  {activeSeasons.length > 0 ? (
                    <Input
                      label="Year"
                      type="number"
                      min={2024}
                      max={2100}
                      value={String(seasonYear)}
                      onChange={(event) => setSeasonYear(Number(event.target.value) || new Date().getFullYear())}
                    />
                  ) : null}
                  {activeLocations.length > 0 ? (
                    <SelectField
                      label="Location"
                      value={formState.locationLabel}
                      options={[
                        { value: '', label: 'Choose location' },
                        ...activeLocations.map((location) => ({ value: formatLocationPresetValue(location), label: formatLocationPresetLabel(location) })),
                      ]}
                      onChange={applyLocationPreset}
                    />
                  ) : (
                    <Input
                      label="Location"
                      hint="Court, park, academy, or meeting place."
                      leftIcon={<MapPin className="h-4 w-4" />}
                      value={formState.locationLabel}
                      onChange={(event) => setField('locationLabel', event.target.value)}
                    />
                  )}
                </div>
              </FormGroup>

              <FormGroup title="Dates and capacity">
                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    label="Start"
                    type="date"
                    value={formState.startsOn}
                    onChange={(event) => setField('startsOn', event.target.value)}
                  />
                  <Input
                    label="End"
                    type="date"
                    value={formState.endsOn}
                    onChange={(event) => setField('endsOn', event.target.value)}
                  />
                  <Input
                    label="Max slots"
                    type="number"
                    min={1}
                    hint="Total available spots."
                    leftIcon={<Users className="h-4 w-4" />}
                    value={formState.defaultCapacity}
                    onChange={(event) => setField('defaultCapacity', event.target.value)}
                  />
                </div>
              </FormGroup>

              <details className="mt-5 rounded-lg border border-border bg-surface px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-text">Classification</summary>
                <div className="mt-4 grid gap-4 md:grid-cols-3">
                  <SelectField
                    label="Sport"
                    value={formState.sportCode}
                    options={sportOptions}
                    onChange={(value) => setField('sportCode', value)}
                  />
                  <SelectField
                    label="Type"
                    value={formState.programType}
                    options={programTypeOptions}
                    onChange={(value) => setField('programType', value)}
                  />
                  <SelectField
                    label="Age"
                    value={formState.ageBandCode}
                    options={ageBandOptions}
                    onChange={(value) => setField('ageBandCode', value)}
                  />
                </div>
              </details>
            </FormSection>
          ) : null}

          {activeStep === 'sessions' ? (
            <OptionPanel
              title="Season blocks"
              actionLabel="Add block"
              onAdd={() => onChange((current) => ({ ...current, sessions: [...current.sessions, createSessionDraft(current.sessions.length)] }))}
            >
              {formState.sessions.map((session, index) => (
                <SessionOptionEditor
                  key={session.id}
                  session={session}
                  index={index}
                  canRemove={formState.sessions.length > 1}
                  onChange={updateSession}
                  onRemove={(id) => onChange((current) => ({ ...current, sessions: current.sessions.filter((item) => item.id !== id) }))}
                />
              ))}
            </OptionPanel>
          ) : null}

          {activeStep === 'classes' ? (
            <OptionPanel
              title="Class options"
              actionLabel="Add class"
              onAdd={() => onChange((current) => ({ ...current, categories: [...current.categories, createCategoryDraft(current.categories.length)] }))}
            >
              {activeCategories.length > 0 ? (
                <div className="rounded-lg border border-border bg-surface p-3">
                  <SelectField
                    label="Add saved category"
                    value=""
                    options={[
                      { value: '', label: 'Choose saved category' },
                      ...activeCategories.map((category) => ({ value: String(category.categoryId), label: formatCategoryPresetLabel(category) })),
                    ]}
                    onChange={applyCategoryPreset}
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-border bg-surface px-3 py-3 text-sm font-medium text-text-muted">
                  <span className="inline-flex items-center gap-2">
                    <Settings className="h-4 w-4 text-primary" />
                    Add reusable class categories in Studio Settings.
                  </span>
                </div>
              )}
              {formState.categories.map((category, index) => (
                <CategoryOptionEditor
                  key={category.id}
                  category={category}
                  index={index}
                  canRemove={formState.categories.length > 1}
                  coachOptions={coachOptions}
                  onChange={updateCategory}
                  onRemove={(id) => onChange((current) => ({ ...current, categories: current.categories.filter((item) => item.id !== id) }))}
                />
              ))}
            </OptionPanel>
          ) : null}

          {activeStep === 'publish' ? (
            <FormSection title="Publish" subtitle="Review readiness and the public registration copy.">
              <FormGroup title="Public details">
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    label="Public title"
                    hint="Shown on the public registration page."
                    value={formState.publicTitle}
                    onChange={(event) => setField('publicTitle', event.target.value)}
                  />
                  <Input
                    label="Season label"
                    hint="Shown with the public class."
                    value={formState.seasonLabel}
                    onChange={(event) => setField('seasonLabel', event.target.value)}
                  />
                  <Input
                    label="Audience"
                    hint="Who this class is for."
                    value={formState.audienceLabel}
                    onChange={(event) => setField('audienceLabel', event.target.value)}
                  />
                  <Input
                    label="Fee label"
                    hint="Short display text, such as a starting price."
                    leftIcon={<DollarSign className="h-4 w-4" />}
                    value={formState.priceLabel}
                    onChange={(event) => setField('priceLabel', event.target.value)}
                  />
                </div>
                <Textarea
                  className="mt-4"
                  label="Public note"
                  hint="One short registration summary for families."
                  value={formState.publicNote}
                  onChange={(event) => setField('publicNote', event.target.value)}
                />
              </FormGroup>

              <FormGroup title="Readiness">
                <div className="grid gap-3 md:grid-cols-2">
                  {readiness.map((item) => (
                    <CheckRow key={item.label} label={item.label} done={item.done} />
                  ))}
                </div>
              </FormGroup>

              <FormGroup title="Publish options">
                <div className="grid gap-3 md:grid-cols-2">
                  <ToggleRow
                    label="Notify families"
                    checked={formState.notifyFamilies}
                    onChange={(checked) => setField('notifyFamilies', checked)}
                  />
                  <ToggleRow
                    label="Ask for day preference"
                    checked={formState.requireDaysPreference}
                    onChange={(checked) => setField('requireDaysPreference', checked)}
                  />
                </div>
              </FormGroup>
            </FormSection>
          ) : null}
        </div>
      </div>

      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error}</p> : null}

      <div className="sticky bottom-0 z-10 -mx-4 border-t border-border bg-white/95 px-4 py-4 backdrop-blur md:-mx-5 md:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="outline" isLoading={isSaving} onClick={() => onSubmit('draft')}>
              Save Draft
            </Button>
            {activeStepIndex > 0 ? (
              <Button type="button" variant="outline" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={goBack}>
                Back
              </Button>
            ) : null}
            {activeStep === 'publish' ? (
              <Button type="submit" isLoading={isSaving}>
                {editingProgram ? 'Publish Changes' : 'Publish'}
              </Button>
            ) : (
              <Button type="submit" rightIcon={<ArrowRight className="h-4 w-4" />}>
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}

function StepButton({
  active,
  complete,
  label,
  onClick,
}: {
  active: boolean;
  complete: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn(
        'flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm font-semibold transition-colors',
        active
          ? 'border-primary bg-primary/5 text-primary'
          : 'border-border bg-white text-text-muted hover:border-primary/40 hover:text-text'
      )}
      onClick={onClick}
    >
      <span>{label}</span>
      {complete ? <CheckCircle2 className="h-4 w-4 text-primary" /> : null}
    </button>
  );
}

function FormSection({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-white p-4 md:p-5">
      <h3 className="text-base font-semibold text-text">{title}</h3>
      {subtitle ? <p className="mt-1 text-sm text-text-muted">{subtitle}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function FormGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface-elevated/20 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">{title}</p>
      {children}
    </div>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1.5">
      <span className="block text-sm font-medium text-text">{label}</span>
      <select
        className="h-10 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-text focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.value || 'blank'} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function OptionPanel({
  title,
  actionLabel,
  onAdd,
  children,
}: {
  title: string;
  actionLabel: string;
  onAdd: () => void;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-white p-4 md:p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-text">{title}</h3>
        <Button type="button" size="sm" variant="outline" leftIcon={<Plus className="h-4 w-4" />} onClick={onAdd}>
          {actionLabel}
        </Button>
      </div>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function SessionOptionEditor({
  session,
  index,
  canRemove,
  onChange,
  onRemove,
}: {
  session: ProgramCatalogSessionDraft;
  index: number;
  canRemove: boolean;
  onChange: (id: string, patch: Partial<ProgramCatalogSessionDraft>) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Block {index + 1}</p>
        {canRemove ? (
          <button type="button" className="text-text-subtle transition-colors hover:text-red-500" onClick={() => onRemove(session.id)} aria-label="Remove block">
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Input label="Block name" value={session.label} onChange={(event) => onChange(session.id, { label: event.target.value })} />
        <Input label="Date label" hint="Short label shown to families." value={session.dateLabel} onChange={(event) => onChange(session.id, { dateLabel: event.target.value })} />
        <Input label="Start" type="date" value={session.startsOn} onChange={(event) => onChange(session.id, { startsOn: event.target.value })} />
        <Input label="End" type="date" value={session.endsOn} onChange={(event) => onChange(session.id, { endsOn: event.target.value })} />
        <Input label="Time" hint="Day/time label for this session." leftIcon={<CalendarDays className="h-4 w-4" />} value={session.scheduleLabel} onChange={(event) => onChange(session.id, { scheduleLabel: event.target.value })} />
        <Input label="Slots" type="number" min={1} hint="Leave blank to use program max." value={session.capacity} onChange={(event) => onChange(session.id, { capacity: event.target.value })} />
      </div>
    </div>
  );
}

function CategoryOptionEditor({
  category,
  index,
  canRemove,
  coachOptions,
  onChange,
  onRemove,
}: {
  category: ProgramCatalogCategoryDraft;
  index: number;
  canRemove: boolean;
  coachOptions: ProgramCatalogCoachOption[];
  onChange: (id: string, patch: Partial<ProgramCatalogCategoryDraft>) => void;
  onRemove: (id: string) => void;
}) {
  const handleCoachChange = (value: string) => {
    const selectedCoach = coachOptions.find((option) => String(option.userId) === value);
    onChange(category.id, {
      coachUserId: value,
      coachLabel: selectedCoach?.label ?? '',
    });
  };

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-text-subtle">Class {index + 1}</p>
        {canRemove ? (
          <button type="button" className="text-text-subtle transition-colors hover:text-red-500" onClick={() => onRemove(category.id)} aria-label="Remove class">
            <Trash2 className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        <Input label="Class name" value={category.label} onChange={(event) => onChange(category.id, { label: event.target.value })} />
        <Input label="Age" hint="Age band shown to families." value={category.ageLabel} onChange={(event) => onChange(category.id, { ageLabel: event.target.value })} />
        <Input label="Level" hint="Ball color, pathway, or skill level." value={category.levelLabel} onChange={(event) => onChange(category.id, { levelLabel: event.target.value })} />
        <Input label="Fee" type="number" min={0} hint="Numeric price." value={category.priceAmount} onChange={(event) => onChange(category.id, { priceAmount: event.target.value })} />
        <Input label="Slots" type="number" min={1} hint="Leave blank to use program max." value={category.capacity} onChange={(event) => onChange(category.id, { capacity: event.target.value })} />
        {coachOptions.length > 0 ? (
          <SelectField
            label="Coach"
            value={category.coachUserId}
            options={[
              { value: '', label: 'Any coach' },
              ...coachOptions.map((coach) => ({ value: String(coach.userId), label: `${coach.label} - ${coach.roleLabel}` })),
            ]}
            onChange={handleCoachChange}
          />
        ) : (
          <Input label="Coach" hint="Optional coach label." value={category.coachLabel} onChange={(event) => onChange(category.id, { coachLabel: event.target.value })} />
        )}
      </div>
      <details className="mt-4 rounded-lg border border-border bg-white px-3 py-3">
        <summary className="cursor-pointer text-sm font-semibold text-text">Schedule and display details</summary>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Input label="Fee text" hint="Optional display label." value={category.priceLabel} onChange={(event) => onChange(category.id, { priceLabel: event.target.value })} />
          <Input label="Time" hint="Class time shown to families." value={category.scheduleLabel} onChange={(event) => onChange(category.id, { scheduleLabel: event.target.value })} />
          <Input label="Days" hint="Day rule for 2-day or 4-day options." value={category.daysRule} onChange={(event) => onChange(category.id, { daysRule: event.target.value })} />
        </div>
      </details>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface px-3 py-3 text-sm font-semibold text-text">
      <span>{label}</span>
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-border text-primary focus:ring-primary/30"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

function CheckRow({ label, done, compact = false }: { label: string; done: boolean; compact?: boolean }) {
  return (
    <div className={cn('flex items-center justify-between gap-3 rounded-lg border border-border bg-surface', compact ? 'px-2 py-1.5' : 'px-3 py-2')}>
      <span className={cn('font-medium text-text', compact ? 'text-xs' : 'text-sm')}>{label}</span>
      <span className={done ? 'text-xs font-semibold uppercase tracking-[0.14em] text-primary' : 'text-xs font-semibold uppercase tracking-[0.14em] text-text-subtle'}>
        {done ? 'Ready' : 'Needed'}
      </span>
    </div>
  );
}

function createCategoryDraftFromPreset(category: TenantStudioCategory, index: number): ProgramCatalogCategoryDraft {
  return {
    id: createCategoryDraft(index).id,
    optionId: `category-${category.categoryId}`,
    label: category.displayName,
    ageLabel: category.ageLabel ?? '',
    levelLabel: category.levelLabel ?? '',
    priceAmount: category.priceAmount != null ? String(category.priceAmount) : '',
    priceLabel: category.priceLabel ?? '',
    coachUserId: '',
    coachLabel: '',
    scheduleLabel: category.scheduleLabel ?? '',
    daysRule: category.daysRule ?? '',
    capacity: category.capacity != null ? String(category.capacity) : '',
    status: 'OPEN',
  };
}

function isBlankCategoryDraft(category: ProgramCatalogCategoryDraft): boolean {
  return !category.label.trim() || category.label === 'Junior class' || category.label.startsWith('Class option ');
}

function inferSeasonYear(value?: string | null): number {
  if (value) {
    const year = Number(value.slice(0, 4));
    if (Number.isFinite(year) && year > 2000) {
      return year;
    }
  }
  return new Date().getFullYear();
}

function resolveEndYear(startYear: number, season: TenantStudioSeason): number {
  if (season.startsMonth && season.endsMonth && season.endsMonth < season.startsMonth) {
    return startYear + 1;
  }
  return startYear;
}

function dateFromSeasonPoint(year: number, month?: number | null, day?: number | null): string | null {
  if (!month || !day) {
    return null;
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatSeasonPresetLabel(season: TenantStudioSeason): string {
  const start = season.startsMonth && season.startsDay ? `${season.startsMonth}/${season.startsDay}` : '';
  const end = season.endsMonth && season.endsDay ? `${season.endsMonth}/${season.endsDay}` : '';
  return [season.displayName, start && end ? `${start} - ${end}` : ''].filter(Boolean).join(' - ');
}

function formatLocationPresetValue(location: TenantStudioLocation): string {
  return [location.displayName, location.courtLabel].filter(Boolean).join(' - ');
}

function formatLocationPresetLabel(location: TenantStudioLocation): string {
  return [formatLocationPresetValue(location), location.zipcode].filter(Boolean).join(' - ');
}

function formatCategoryPresetLabel(category: TenantStudioCategory): string {
  return [
    category.displayName,
    category.ageLabel,
    category.levelLabel,
    category.priceLabel || (category.priceAmount != null ? `$${category.priceAmount}` : ''),
  ]
    .filter(Boolean)
    .join(' - ');
}
